<template>
	<div
		class="quick-actions"
		data-test="quick-actions"
	>
		<template v-if="ready">
			<router-link
				v-for="action in actions"
				:key="action.id"
				:to="action.to"
				class="quick-actions__chip sgnl-text-color-default"
				:data-test="`quick-actions-chip-${action.id}`"
				@click="$posthog?.capture('home_quick_action_clicked', { action_id: action.id, phase: action.phase })"
			>
				<div class="quick-actions__icon">
					<component :is="action.icon" />
				</div>
				<div class="quick-actions__text">
					<div class="quick-actions__title sgnl-text-color-default sgnl-paragraph-s-medium">
						{{ action.title }}
					</div>
					<div class="quick-actions__sub sgnl-text-color-secondary sgnl-caption-regular">
						{{ action.sub }}
					</div>
				</div>
				<ArrowRight16 class="quick-actions__chevron" />
			</router-link>
		</template>
		<template v-else>
			<div
				v-for="i in 4"
				:key="i"
				class="quick-actions__chip quick-actions__chip--skeleton sgnl-text-color-default"
				aria-hidden="true"
			>
				<SkeletonBlock
					class="quick-actions__icon-skeleton"
					width="var(--sgnl-unit-36)"
					height="var(--sgnl-unit-36)"
					radius="var(--sgnl-border-radius-6)"
				/>
				<div class="quick-actions__text">
					<SkeletonBlock
						class="quick-actions__title sgnl-text-color-default sgnl-paragraph-s-medium"
						width="80%"
						radius="var(--sgnl-border-radius-4)"
					/>
					<SkeletonBlock
						class="quick-actions__sub sgnl-text-color-secondary sgnl-caption-regular"
						width="60%"
						radius="var(--sgnl-border-radius-4)"
					/>
				</div>
				<ArrowRight16 class="quick-actions__chevron" />
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType } from "vue";
import type { RouteLocationRaw } from "vue-router";

import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import Password16 from "@carbon/icons-vue/es/password/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";
import Db2Database16 from "@carbon/icons-vue/es/db2--database/16";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";
import View16 from "@carbon/icons-vue/es/view/16";
import Play16 from "@carbon/icons-vue/es/play/16";
import Add16 from "@carbon/icons-vue/es/add/16";
import Purchase16 from "@carbon/icons-vue/es/purchase/16";

export type QuickActionUserState = {
	hasApiKey: boolean;
	onboarded: boolean;
	customCores: number;
	dataSources: number;
	repos: number;
	tasksRun: number;
	lastTask: { id: string; whenLabel: string; core: string } | null;
	lastRepoName: string | null;
	lastRepoId: string | null;
	isEnterprise: boolean;
	quotaNearLimit: boolean;
	quotaAtLimit: boolean;
};

type ActionDef = {
	id: string;
	icon: any;
	title: string | ((s: QuickActionUserState) => string);
	weight: number;
	phase: "setup" | "active" | "any";
	to: RouteLocationRaw | ((s: QuickActionUserState) => RouteLocationRaw);
	available?: (s: QuickActionUserState) => boolean;
	sub: (s: QuickActionUserState) => string;
};

const ACTION_POOL: ActionDef[] = [
	{
		id: "create-key",
		icon: markRaw(Password16),
		title: "Create API key",
		phase: "setup",
		weight: 0,
		to: { name: "Settings_cloudEngineAPI" },
		sub: (s) => (s.hasApiKey ? "Compute Engine API key active" : "For Compute Engine API access"),
	},
	{
		id: "config-core",
		icon: markRaw(Chip16),
		title: "Configure custom core",
		phase: "setup",
		weight: 1,
		to: "/cores",
		sub: (s) => (s.customCores ? `${s.customCores} custom cores in use` : "Precision, memory, architecture"),
	},
	{
		id: "connect-data",
		icon: markRaw(Db2Database16),
		title: "Connect data source",
		phase: "setup",
		weight: 2,
		to: "/datasources",
		sub: (s) => (s.dataSources ? `${s.dataSources} sources connected` : "No sources connected yet"),
	},
	{
		id: "connect-repo",
		icon: markRaw(LogoGithub16),
		title: "Connect repository",
		phase: "setup",
		weight: 3,
		to: "/repositories",
		sub: (s) => (s.repos ? `${s.repos} repos connected` : "Login with GitHub"),
	},
	{
		id: "view-last-task",
		icon: markRaw(View16),
		title: "View last task",
		phase: "active",
		weight: 0,
		to: (s) => (s.lastTask ? { name: "TaskDetail", params: { id: s.lastTask.id } } : "/tasks"),
		available: (s) => !!s.lastTask,
		sub: (s) => (s.lastTask ? `${s.lastTask.core} · ${s.lastTask.whenLabel}` : "Open a previous task"),
	},
	{
		id: "upgrade-account",
		icon: markRaw(Purchase16),
		title: "Upgrade Account",
		phase: "any",
		weight: 1,
		to: "/billing",
		available: (s) => s.quotaNearLimit || s.quotaAtLimit,
		sub: (s) => (s.quotaAtLimit ? "You hit your limit." : "You are close to your limit."),
	},
	{
		id: "invite-team",
		icon: markRaw(Add16),
		title: "Invite a teammate",
		phase: "active",
		weight: 2,
		to: { name: "Settings_organizations" },
		available: (s) => s.isEnterprise,
		sub: () => "Share access to this organization",
	},
	{
		id: "open-repo",
		icon: markRaw(LogoGithub16),
		title: (s) => (s.lastRepoId ? "Open last repository" : "Open repositories"),
		phase: "active",
		weight: 3,
		to: (s) =>
			s.lastRepoId ? { name: "RepositoryDetail", query: { repositoryID: s.lastRepoId } } : "/repositories",
		sub: (s) => s.lastRepoName ?? (s.repos > 0 ? `${s.repos} connected` : "Connect a GitHub repository"),
	},
	{
		id: "open-data-sources",
		icon: markRaw(Db2Database16),
		title: "Open data sources",
		phase: "active",
		weight: 4,
		to: "/datasources",
		sub: (s) => (s.dataSources ? `${s.dataSources} sources connected` : "Drives, buckets, gateways"),
	},
	{
		id: "browse-cores",
		icon: markRaw(Chip16),
		title: "Browse cores",
		phase: "active",
		weight: 5,
		to: "/cores",
		sub: (s) => (s.customCores ? `${s.customCores} custom cores in use` : "Configure custom cores"),
	},
	{
		id: "open-playground",
		icon: markRaw(Play16),
		title: "Open Code Playground",
		phase: "any",
		weight: 6,
		to: { name: "CodeEditor" },
		sub: () => "Try code in the browser",
	},
];

export default defineComponent({
	name: "QuickActions",
	components: { ArrowRight16, SkeletonBlock },
	props: {
		userState: {
			type: Object as PropType<QuickActionUserState>,
			required: true,
		},
		ready: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		actions(): {
			id: string;
			icon: any;
			title: string;
			sub: string;
			to: RouteLocationRaw;
			phase: ActionDef["phase"];
		}[] {
			const s = this.userState;
			const candidates = ACTION_POOL.filter((a) => {
				if (a.available && !a.available(s)) return false;
				if (!s.onboarded) return a.phase === "setup" || a.phase === "any";
				return a.phase === "active" || a.phase === "any";
			})
				.sort((a, b) => a.weight - b.weight)
				.slice(0, 4);
			return candidates.map((a) => ({
				id: a.id,
				icon: a.icon,
				title: typeof a.title === "function" ? a.title(s) : a.title,
				to: typeof a.to === "function" ? a.to(s) : a.to,
				sub: a.sub(s),
				phase: a.phase,
			}));
		},
	},
});
</script>

<style lang="scss" scoped>
.quick-actions {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: var(--sgnl-space-16);
}

.quick-actions__chip {
	background: var(--sgnl-default-primary-background);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-6);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-12);
	text-decoration: none;
	min-width: 0;
	transition:
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		border-color var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		box-shadow var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard);
}

.quick-actions__chip:hover {
	border-color: var(--sgnl-default-border-hover);
	box-shadow: 0 0 0 var(--sgnl-border-width-2) var(--sgnl-focus-ring-secondary);
}

.quick-actions__chip:active {
	transform: scale(0.985);
}

.quick-actions__chip--skeleton {
	cursor: default;
	pointer-events: none;
}

.quick-actions__chip--skeleton:hover {
	border-color: var(--sgnl-default-secondary-border);
	box-shadow: none;
}

.quick-actions__chip:hover .quick-actions__title {
	text-decoration: underline;
}

.quick-actions__icon {
	width: var(--sgnl-unit-36);
	height: var(--sgnl-unit-36);
	border-radius: var(--sgnl-border-radius-6);
	display: grid;
	place-items: center;
	flex-shrink: 0;
	background: var(--sgnl-brand-background-inverse);
	color: var(--sgnl-brand-icon);
}

.quick-actions__text {
	min-width: 0;
	flex: 1;
}

.quick-actions__sub {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.quick-actions__chevron {
	color: var(--sgnl-default-secondary-text);
	flex-shrink: 0;
}

@media (max-width: 1099px) {
	.quick-actions {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 719px) {
	.quick-actions {
		grid-template-columns: 1fr;
	}
}
</style>
